import type {
  EnvironmentId,
  ProjectId,
  ScopedProjectRef,
  ScopedThreadRef,
  ThreadId,
} from "@cmmd-forge/contracts";

export function scopeProjectRef(
  environmentId: EnvironmentId,
  projectId: ProjectId,
): ScopedProjectRef {
  return { environmentId, projectId };
}

export function scopeThreadRef(environmentId: EnvironmentId, threadId: ThreadId): ScopedThreadRef {
  return { environmentId, threadId };
}

const VERSIONED_SCOPED_KEY_PREFIX = "scoped:v1:";

export function scopedRefKey(ref: ScopedProjectRef | ScopedThreadRef): string {
  const localId = "projectId" in ref ? ref.projectId : ref.threadId;
  if (localId.includes(":") || ref.environmentId.startsWith(VERSIONED_SCOPED_KEY_PREFIX)) {
    return `${VERSIONED_SCOPED_KEY_PREFIX}${encodeURIComponent(ref.environmentId)}:${encodeURIComponent(localId)}`;
  }
  return `${ref.environmentId}:${localId}`;
}

export function scopedProjectKey(ref: ScopedProjectRef): string {
  return scopedRefKey(ref);
}

export function scopedThreadKey(ref: ScopedThreadRef): string {
  return scopedRefKey(ref);
}

function parseVersionedScopedKey(
  key: string,
): { environmentId: EnvironmentId; localId: string } | null {
  const payload = key.slice(VERSIONED_SCOPED_KEY_PREFIX.length);
  const separatorIndex = payload.indexOf(":");
  if (
    separatorIndex <= 0 ||
    separatorIndex >= payload.length - 1 ||
    payload.indexOf(":", separatorIndex + 1) !== -1
  ) {
    return null;
  }
  try {
    const environmentId = decodeURIComponent(payload.slice(0, separatorIndex));
    const localId = decodeURIComponent(payload.slice(separatorIndex + 1));
    return environmentId.length > 0 && localId.length > 0
      ? { environmentId: environmentId as EnvironmentId, localId }
      : null;
  } catch {
    return null;
  }
}

function parseScopedKey(key: string): { environmentId: EnvironmentId; localId: string } | null {
  if (key.startsWith(VERSIONED_SCOPED_KEY_PREFIX)) {
    return parseVersionedScopedKey(key);
  }

  // Legacy keys are right-delimited because runtime environment ids may contain colons.
  const separatorIndex = key.lastIndexOf(":");
  if (separatorIndex <= 0 || separatorIndex >= key.length - 1) {
    return null;
  }
  return {
    environmentId: key.slice(0, separatorIndex) as EnvironmentId,
    localId: key.slice(separatorIndex + 1),
  };
}

export function parseScopedProjectKey(key: string): ScopedProjectRef | null {
  const parsed = parseScopedKey(key);
  if (!parsed) {
    return null;
  }
  return {
    environmentId: parsed.environmentId,
    projectId: parsed.localId as ProjectId,
  };
}

export function parseScopedThreadKey(key: string): ScopedThreadRef | null {
  const parsed = parseScopedKey(key);
  if (!parsed) {
    return null;
  }
  return {
    environmentId: parsed.environmentId,
    threadId: parsed.localId as ThreadId,
  };
}
